import { FC, useMemo } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import { Formik, FormikHelpers } from "formik";
import { Navigate } from "react-router-dom";
import yup from "app/util/yup";
import { NavigationScreen } from "app/component/screen/NavigationScreen";
import { RouteBreadcrumbs } from "app/route/RouteBreadcrumbs";
import { editUserRoute, userRoute } from "app/route/Routes";
import { useRouteParams } from "app/route/route";
import { useUpdateUserMutation, useUserQuery } from "app/api/graph/types";
import { userGraphId } from "app/api/graph/helpers";
import { LoadingScreen } from "app/component/screen/LoadingScreen";
import { ErrorScreen } from "app/component/screen/ErrorScreen";
import { SelectRoles } from "app/component/form/select/SelectRoles";
import { handleError, handleSuccess } from "app/api/graph/form";
import { StatusAlert } from "app/component/form/StatusAlert";
import { AllUserRolePermissionsOverviewCard } from "app/component/user/AllUserRolePermissionsOverviewCard";

type FormValues = {
    roles: string[];
    disabled: boolean;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    roles: yup.array().of(yup.string().required()).required(),
    disabled: yup.boolean().defined(),
});

export const EditUserScreen: FC = () => {
    const { userId } = useRouteParams(editUserRoute);
    const { data, loading, error } = useUserQuery({ variables: { userId: userGraphId(userId) } });
    const [updateUser, updateUserResult] = useUpdateUserMutation();
    const user = data?.user;

    const initialValues = useMemo(
        () => ({
            roles: user?.roles ?? [],
            disabled: user?.disabled ?? false,
        }),
        [user],
    );

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        updateUser({ variables: { input: { id: userGraphId(userId), ...values } } })
            .then(() => handleSuccess(actions, "User updated"))
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    if (loading) {
        return <LoadingScreen />;
    }
    if (error) {
        return <ErrorScreen title="Error" message={error.message} />;
    }
    if (!user) {
        return <ErrorScreen title="Error" message="Not found." />;
    }
    if (updateUserResult.data?.updateUser?.user != null) {
        return <Navigate to={userRoute.build({ userId })} />;
    }

    return (
        <NavigationScreen>
            <Container>
                <RouteBreadcrumbs route={editUserRoute} />
                <h1>{user.fullName}</h1>
                <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
                    {({
                        handleSubmit,
                        setFieldValue,
                        setFieldTouched,
                        values,
                        touched,
                        errors,
                        isSubmitting,
                        status,
                        handleChange,
                        handleBlur,
                    }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <StatusAlert status={status as unknown} />

                            <Form.Group className="mb-3">
                                <Form.Check
                                    id="disabled"
                                    type="checkbox"
                                    name="disabled"
                                    label="Disabled"
                                    checked={values.disabled ?? false}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={touched.disabled && !!errors.disabled}
                                    feedback={errors.disabled}
                                />
                                <Form.Text className="text-muted">
                                    Disabling a user will mean that they are unable to log in and access the system.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Row>
                                    <Col>
                                        <Form.Label>Roles</Form.Label>
                                        <SelectRoles
                                            id="select-roles"
                                            placeholder="Roles"
                                            roles={values.roles}
                                            onChange={(roles) => setFieldValue("roles", roles)}
                                            onBlur={() => setFieldTouched("roles")}
                                            isInvalid={touched.roles && !!errors.roles}
                                        />
                                        <Form.Text className="text-muted">
                                            Role changes won&lsquo;t take effect until the next time the user logs in.
                                        </Form.Text>
                                        <Form.Control.Feedback type="invalid">{errors.roles}</Form.Control.Feedback>
                                    </Col>
                                    <Col>
                                        <AllUserRolePermissionsOverviewCard />
                                    </Col>
                                </Row>
                            </Form.Group>

                            <Button type="submit" disabled={isSubmitting}>
                                Submit
                            </Button>
                        </Form>
                    )}
                </Formik>
            </Container>
        </NavigationScreen>
    );
};
